import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { useEffect, useSyncExternalStore } from 'react'
import { z } from 'zod'

import { api, isSignedIn, loadTokens, peekTokens, subscribeTokens } from '@/shared/api'

import {
  notificationItem,
  notificationsPage,
  unreadCountResponse,
  type NotificationItem,
} from './schemas'

export const notificationKeys = {
  all: ['notifications'] as const,
  inbox: () => [...notificationKeys.all, 'inbox'] as const,
  unread: () => [...notificationKeys.all, 'unread-count'] as const,
}

/** §18's sample page size. Core caps a page at fifty. */
const PAGE_SIZE = 20

/**
 * `GET /notifications`, cursor-paged like the transaction feed: `?after=` is
 * the last id of the page before, and a `null` cursor is the end, so
 * `hasNextPage` is the honest end-of-inbox signal.
 */
export const inboxQuery = infiniteQueryOptions({
  queryKey: notificationKeys.inbox(),
  queryFn: ({ pageParam, signal }) =>
    api.get('/notifications', {
      schema: notificationsPage,
      query: { limit: PAGE_SIZE, after: pageParam },
      signal,
    }),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  staleTime: 30_000,
})

/**
 * `GET /notifications/unread-count`: the bell's badge, re-read every minute
 * while the page is visible and again when it becomes visible, which is what
 * TanStack Query means by focus.
 *
 * Not retried: the badge is a hint, and the next poll is its retry.
 */
export const unreadCountQuery = queryOptions({
  queryKey: notificationKeys.unread(),
  queryFn: ({ signal }) =>
    api.get('/notifications/unread-count', { schema: unreadCountResponse, signal }),
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
  retry: false,
})

/**
 * The count the bell shows, read **only while there is a session**, and `0`,
 * so no badge, whenever there is nothing current to show.
 *
 * `AppTopBar` is on screens outside `RequireSession` too (the deposit and
 * withdrawal flows), so the bar cannot assume a signed-in user. Half of a
 * two-step login holds tokens without being a session, so the question is
 * `isSignedIn`, not "are there tokens", and it is asked of the store on every
 * change: a `401` or a logout that clears the tokens stops the poll at once.
 */
export function useUnreadCount(): number {
  const signedIn = useSyncExternalStore(subscribeTokens, () => isSignedIn(peekTokens()))

  // The store is filled lazily; a cold load of a screen outside the shell may
  // reach this before anything else has read it.
  useEffect(() => {
    void loadTokens()
  }, [])

  const { data, isError } = useQuery({ ...unreadCountQuery, enabled: signedIn })
  // A disabled query still holds what it last read, so the session is asked
  // here too: the badge goes with it. A failed read hides it as well.
  return signedIn && !isError ? (data?.unread ?? 0) : 0
}

/** Applies `edit` to every cached row of the inbox, on every loaded page. */
function editInbox(
  queryClient: QueryClient,
  edit: (item: NotificationItem) => NotificationItem,
) {
  queryClient.setQueryData(inboxQuery.queryKey, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({ ...page, items: page.items.map(edit) })),
        }
      : data,
  )
}

/**
 * `POST /notifications/{id}/read`, sent when a row is opened. Nobody waits on
 * it: the row navigates at once, and the server's answer replaces the cached
 * row whenever it lands. The callbacks are the hook's own, not `mutate`'s, so
 * they still run after the inbox has unmounted.
 */
export function useMarkRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/notifications/${id}/read`, { schema: notificationItem }),
    onSuccess: (read) => {
      editInbox(queryClient, (item) => (item.id === read.id ? read : item))
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unread() })
    },
  })
}

/**
 * `POST /notifications/read-all`, `204` with no body, so the schema says there
 * is nothing to read.
 *
 * `unread` is the set of rows the inbox showed as unread when it sent this.
 * Only those are marked read in the cache: a page loaded since keeps the
 * `is_read` it was served with. The inbox keeps drawing their dots for the
 * rest of the visit from the same set, which is the mutation's `variables`.
 */
export function useMarkAllRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (_unread: ReadonlySet<string>) =>
      api.post('/notifications/read-all', { schema: z.void() }),
    onSuccess: (_, unread) => {
      editInbox(queryClient, (item) =>
        unread.has(item.id) ? { ...item, is_read: true } : item,
      )
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unread() })
    },
  })
}
